import { CommonModule } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzModalModule, NzModalRef } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from 'ng-zorro-antd/table';
import { Subject, takeUntil } from 'rxjs';
import { ICoding } from 'src/app/interfaces/ICoding';
import { IAddDietaryHabits } from 'src/app/interfaces/SocialHistory/DietaryHabits/IAddDietaryHabits';
import { DietaryHabitsService } from 'src/app/services/dietaryHabits.service';
import { EpisodeOfCareService } from 'src/app/services/episodeOfCare.service';
import { PatientService } from 'src/app/services/patient.service';
import { SwalToastService } from 'src/app/services/swal.service';

@Component({
  selector: 'app-dietary-habits-add-edit',
  standalone: true,
  imports: [
    NzModalModule,
    TranslateModule,
    FormsModule,
    FlexLayoutModule,
    ReactiveFormsModule,
    NzSelectModule,
    CommonModule,
    NzButtonModule,
    NzTableModule,
    NzLayoutModule,
    NzIconModule,
  ],
  templateUrl: './dietary-habits-add-edit.component.html',
  styleUrls: ['./dietary-habits-add-edit.component.css'],
})
export class DietaryHabitsAddEditComponent {
  private destroy$ = new Subject<void>();
  public dietaryHabitsDropdown?: ICoding[];
  public timingUnitDropdown?: ICoding[];
  isVisible = false;
  isLoading = false;
  isDropdownLoading = false;
  isEdit = false;
  editDietaryHabitsId?: number;
  dietaryHabitsSubmitted = false;
  @ViewChild('modalRef', { static: true }) modalRef!: NzModalRef;
  dietaryHabitsForm: FormGroup = new FormGroup({});
  constructor(
    private readonly dietaryHabitsService: DietaryHabitsService,
    private readonly formBuilder: FormBuilder,
    private readonly swalToastService: SwalToastService,
    private readonly translate: TranslateService,
    private readonly patientService: PatientService,
    private readonly eocService: EpisodeOfCareService
  ) {}
  ngOnInit(): void {
    this.isLoading = true;
    this.isEdit = false;
    this.isDropdownLoading = true;
    this.dietaryHabitsForm = this.formBuilder.group({
      dietaryHabitsIdValue: [null, Validators.required],
      unitAmountValue: [
        null,
        Validators.compose([Validators.required, Validators.min(0)]),
      ],
      customTimingUnitIdValue: [null, Validators.required],
      descriptionValue: [null, Validators.required],
    });

    this.dietaryHabitsService.getDietaryHabitsDropdown().subscribe({
      next: (res) => {
        this.dietaryHabitsDropdown = res?.data?.dietTypes;
        const allowedUnits = ['day', 'week', 'month', 'year'];
        this.timingUnitDropdown = res?.data?.timingUnit?.filter(
          (unit: ICoding) =>
            allowedUnits.includes(unit.name?.toLowerCase()?.trim())
        );
        this.isDropdownLoading = false;
      },
      error: (err) => {
        console.error('Error fetching dropdown data:', err);
      },
    });

    this.dietaryHabitsService.dietaryHabitsEditSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        if (data) {
          this.isEdit = true;
          this.editDietaryHabitsId = data?.dietTypeId;
          this.dietaryHabitsForm.patchValue({
            dietaryHabitsIdValue: data?.dietTypeId,
            unitAmountValue: data?.unitAmount,
            customTimingUnitIdValue: data?.customTimingUnitId,
            descriptionValue: data?.description,
          });
        } else {
          this.isEdit = false;
          this.editDietaryHabitsId = undefined;
          this.dietaryHabitsForm.reset();
        }
      });

    this.dietaryHabitsService.dietaryHabitsModalSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((showModal) => {
        this.isVisible = showModal;
      });

    this.isLoading = false;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this?.modalRef.destroy();
  }

  handleCancel() {
    this.dietaryHabitsForm.reset();

    this.dietaryHabitsService.dietaryHabitsModalSub.next(false);

    this.dietaryHabitsSubmitted = false;

    this.isLoading = false;

    this?.modalRef.destroy();
  }

  submitForm() {
    this.isLoading = true;
    this.dietaryHabitsSubmitted = true;
    if (this.dietaryHabitsForm.valid) {
      const dietaryHabitsObj: IAddDietaryHabits = {
        dietTypeId: this.dietaryHabitsForm.value.dietaryHabitsIdValue,
        description: this.dietaryHabitsForm.value.descriptionValue,
        unitAmount: this.dietaryHabitsForm.value.unitAmountValue,
        customTimingUnitId:
          this.dietaryHabitsForm.value.customTimingUnitIdValue,
        patientId: this.patientService.patientIDsignal(),
        visitId: this.eocService.currentVisitId(),
      };

      if (this.isEdit) {
        this.dietaryHabitsService
          .updateDietaryHabits(dietaryHabitsObj, this.editDietaryHabitsId!)
          .subscribe({
            next: () => {
              this.swalToastService.toastSuccess(
                'Dietary Habits edited successfully'
              );
              this.dietaryHabitsService.getData();

              this.handleCancel();
            },
            error: (err) => {
              this.swalToastService.toastError('Unable to edit Dietary Habits');
              this.handleCancel();
            },
          });
      } else {
        this.dietaryHabitsService.addDietaryHabits(dietaryHabitsObj).subscribe({
          next: () => {
            this.swalToastService.toastSuccess(
              'Dietary Habits added successfully'
            );
            this.dietaryHabitsService.getData();

            this.handleCancel();
          },
          error: (err) => {
            this.swalToastService.toastError('Unable to add Dietary Habits');
            this.handleCancel();
          },
        });
      }
    } else {
      this.isLoading = false;
    }
  }
}
